import React from 'react';
import { Integrations } from '../../../../util/constants';
import {
  qbo,
  sage,
  sage200,
  sageintacct,
  sage50,
  xeroblue,
  accountsiq,
  freeagent,
  myobaccountright,
  freshbooks,
  csv,
  sage200cloud,
  zohobooks,
  openapi,
  zapier,
  netsuite,
  d365bc,
  dynamics365businesscentral,
  odoo,
  stripe,
} from '../../../../assets/logos';
import { Stack, Text, Grid, Heading, Tooltip, Position } from '@chaser/components';
import cx from 'classnames';
import style from './Integrations.module.scss';
import useRemoteConfig from '../../../../hooks/useRemoteConfig';
import useUser from '../../../../hooks/useUser';

const IntegrationIconMap = {
  [Integrations.QUICKBOOKS]: { src: qbo, height: '25', width: '130' },
  [Integrations.XERO]: { src: xeroblue, height: '41', width: '41' },
  [Integrations.SAGE]: { src: sage, height: '30', width: '168' },
  [Integrations.SAGEINTACCT]: { src: sageintacct, height: '30', width: '168' },
  [Integrations.SAGE200]: { src: sage200, height: '30', width: '168' },
  [Integrations.SAGE50]: { src: sage50, height: '30', width: '168' },
  [Integrations.ACCOUNTSIQ]: { src: accountsiq, height: '21', width: '104' },
  [Integrations.FREEAGENT]: { src: freeagent, height: '21', width: '104' },
  [Integrations.SAGECLOUD]: { src: sage200cloud, height: '21', width: '104' },
  [Integrations.MYOB]: { src: myobaccountright, height: '35', width: '85' },
  [Integrations.DYNAMICS]: { src: dynamics365businesscentral, height: '38', width: '120' },
  [Integrations.D365BC]: { src: d365bc, height: '38', width: '120' },
  [Integrations.FRESHBOOKS]: { src: freshbooks, height: '38', width: '136' },
  [Integrations.CSV]: { src: csv, height: '38', width: '45' },
  [Integrations.ZOHOBOOKS]: { src: zohobooks, height: '38', width: '45' },
  [Integrations.OPENAPI]: { src: openapi },
  [Integrations.ZAPIER]: { src: zapier },
  [Integrations.NETSUITE]: { src: netsuite, height: '64', width: '64' },
  [Integrations.ODOO]: { src: odoo, height: '41', width: '80' },
  [Integrations.STRIPE]: { src: stripe, height: '41', width: '80' },
};

function IntegrationButton({
  integrationId,
  onIntegrationSelected,
  selectedIntegration,
  disabled,
  adminOnly,
  children,
}) {
  return (
    <button
      data-testid={`${integrationId}-org-add`}
      data-trackingid={`${integrationId}-org-add`}
      type="button"
      disabled={disabled}
      value={integrationId}
      style={{ width: '100%' }}
      className={cx(
        style.button,
        selectedIntegration === integrationId && style.active,
        disabled && style.disabled,
        adminOnly && style.adminOnly,
      )}
      onClick={() => !disabled && onIntegrationSelected(integrationId)}
    >
      {children}
    </button>
  );
}

function getIcon(integrationId) {
  if (IntegrationIconMap[integrationId]) {
    const { src, height, width } = IntegrationIconMap[integrationId];
    return <img alt="" height={height} width={width} src={src} />;
  }

  return null;
}

function getConfigProps(integrations = {}, integrationId) {
  return {
    show: integrations[integrationId]?.show ?? true,
    disabled: integrations[integrationId]?.disabled ?? false,
  };
}

const INTERNAL_INTEGRATIONS = [Integrations.CSV, Integrations.OPENAPI, Integrations.ZAPIER];

const INTEGRATION_TITLE = {
  [Integrations.CSV]:
    'Upload your data via CSV files using either our CSV upload tool or an SFTP server',
  [Integrations.OPENAPI]: 'Seamlessly connect your system to Chaser with our robust API',
  [Integrations.ZAPIER]:
    'Automate workflows by integrating with Chaser or linking Google Sheets/Excel',
};

const INTEGRATION_SUBTITLE = {
  [Integrations.CSV]:
    'Perfect for those using a platform without native integration, allowing easy data uploads through CSV files.',
  [Integrations.OPENAPI]:
    'Ideal for businesses seeking a fully customised and direct connection between Chaser and their existing system.',
  [Integrations.ZAPIER]:
    'Best for automating simple tasks and linking Chaser with other tools, like Google Sheets or Excel, without complex setup.',
};

function IntegrationList({ onIntegrationSelected, selectedIntegration }) {
  const integrationConfig = useRemoteConfig('integrations');
  const { isAdmin } = useUser();

  const externalIntegrations = [];
  const internalIntegrations = [];

  Object.keys(Integrations).forEach(integration => {
    if (INTERNAL_INTEGRATIONS.includes(Integrations[integration])) {
      internalIntegrations.push(integration);
    } else {
      externalIntegrations.push(integration);
    }
  });

  const renderIntegrationButton = integration => {
    const integrationId = Integrations[integration];
    const { disabled, show } = getConfigProps(integrationConfig, integrationId);

    if (!show) return null;

    const title = INTEGRATION_TITLE[integrationId];
    const tooltip = INTEGRATION_SUBTITLE[integrationId];

    return (
      <Stack>
        <Tooltip content={tooltip} disable={!tooltip} position={Position.TOP}>
          <IntegrationButton
            key={integrationId}
            disabled={!isAdmin && disabled}
            adminOnly={isAdmin && !show}
            integrationId={integrationId}
            onIntegrationSelected={onIntegrationSelected}
            selectedIntegration={selectedIntegration}
          >
            {getIcon(integrationId)}
          </IntegrationButton>
        </Tooltip>
        {title && <Text color="Mid Neutral 1">{title}</Text>}
      </Stack>
    );
  };

  return (
    <Stack gap="medium" textAlign="center">
      <Heading as="h3" fontSize="heading.3" fontWeight="bold">
        Connect to Chaser with:
      </Heading>
      <Grid width={'100%'} minChildWidth={225} gap="small">
        {externalIntegrations.map(renderIntegrationButton)}
      </Grid>
      <Heading as="h3" fontSize="heading.3" fontWeight="bold">
        Other ways to connect:
      </Heading>
      <Grid width={'100%'} minChildWidth={225} gap="small">
        {internalIntegrations.map(renderIntegrationButton)}
      </Grid>
    </Stack>
  );
}

export default IntegrationList;
